import React, { useRef, useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import atlasApi from "../../utils/atlasapi";
import { toMultiPolygon, dissolveToMultiPolygon } from "../../utils/geometry";
import {
	MagnifyingGlass, Wind, Lightning, Tree, Shield, Ruler, MapPin,
	X, Plus
} from "@phosphor-icons/react";

import mapboxgl from '!mapbox-gl'; // eslint-disable-line import/no-webpack-loader-syntax

export const DATASETS = {
	all:         { label: "All",         file: "/data/bulgaria_sites.geojson", center: [25.50, 42.70], zoom: 6.8, idPrefix: "BG" },
	prioritized: { label: "Prioritized", file: "/data/zone_sites.geojson",     center: [25.50, 42.70], zoom: 6.8, idPrefix: "BG" }
};

const PRIORITIZED_ZONES_FILE = "/data/bg_prioritized.geojson";
const HIGH_VOLTAGE_FILE = "/data/bg_high_voltage.geojson";

function addHighVoltageLines(map) {
	if (!map.getSource("scr-high-voltage")) {
		map.addSource("scr-high-voltage", { type: "geojson", data: HIGH_VOLTAGE_FILE });
	}
	if (!map.getLayer("scr-high-voltage-line")) {
		map.addLayer({
			id: "scr-high-voltage-line",
			type: "line",
			source: "scr-high-voltage",
			paint: {
				"line-color": "#000000",
				"line-width": 1.2,
				"line-opacity": 0.8
			}
		}, map.getLayer("scr-areas-label") ? "scr-areas-label" : undefined);
	}
}

function addPrioritizedZones(map) {
	if (!map.getSource("scr-prioritized-zones")) {
		map.addSource("scr-prioritized-zones", { type: "geojson", data: PRIORITIZED_ZONES_FILE });
	}
	if (!map.getLayer("scr-prioritized-zones-line")) {
		map.addLayer({
			id: "scr-prioritized-zones-line",
			type: "line",
			source: "scr-prioritized-zones",
			paint: {
				"line-color": "#1d4ed8",
				"line-width": 2,
				"line-opacity": 0.9
			}
		}, map.getLayer("scr-areas-fill") ? "scr-areas-fill" : undefined);
	}
}

function removePrioritizedZones(map) {
	if (map.getLayer("scr-prioritized-zones-line")) map.removeLayer("scr-prioritized-zones-line");
	if (map.getSource("scr-prioritized-zones")) map.removeSource("scr-prioritized-zones");
}

// null = filter off (all areas pass)
const FILTER_DEFAULTS = {
	windSpeedMin: null,
	distLineMax: null,
	distSubstationMax: null,
	distNatureMin: null,
	distMilitaryMin: null,
	areaMin: null,
	areaMax: null,
	municipalities: []
};

function passes(p, f) {
	return (
		(f.windSpeedMin == null || p.windSpeed >= f.windSpeedMin) &&
		(f.distLineMax == null || p.distLine <= f.distLineMax) &&
		(f.distSubstationMax == null || p.distSubstation <= f.distSubstationMax) &&
		(f.distNatureMin == null || p.distNatureReserve >= f.distNatureMin) &&
		(f.distMilitaryMin == null || p.distMilitary >= f.distMilitaryMin) &&
		(f.areaMin == null || p.areaHa >= f.areaMin) &&
		(f.areaMax == null || p.areaHa <= f.areaMax) &&
		(f.municipalities.length === 0 || f.municipalities.includes(p.municipality))
	);
}

// Normalize dataset fields → fields the UI expects.
function normalizeFeatures(geojson, idPrefix = "BG") {
	const features = (geojson.features || []).map((feat) => {
		const p = feat.properties || {};
		const id = idPrefix + "-" + String(p.site_id).padStart(3, "0");
		return {
			...feat,
			properties: {
				id,
				name: p.name || id,
				municipality: p.municipality || "Unknown",
				areaHa: Math.round(p.area_ha || 0),
				windSpeed: +(p.wind_mean || 0),
				distLine: +((p.dist_to_line_m || 0) / 1000),
				distSubstation: +((p.dist_to_substation_m || 0) / 1000),
				distNatureReserve: +(Math.min(
					p.dist_to_spa_m == null ? Infinity : p.dist_to_spa_m,
					p.dist_to_sac_m == null ? Infinity : p.dist_to_sac_m
				) / 1000) || 0,
				distMilitary: +((p.dist_to_military_m || 0) / 1000),
				forestPct: +(p.forest_pct || 0),
				distSettlement: +((p.dist_to_settlement_m || 0) / 1000),
				score: Math.round(p.score || 0)
			}
		};
	});
	return { type: "FeatureCollection", features };
}

/* ---- Filter chips ---- */
function RangeSingle({ label, value, min, max, step, onChange, format }) {
	const pct = ((value - min) / (max - min)) * 100;
	return (
		<>
			<div className="range-label">
				<span>{label}</span>
				<span className="val">{format(value)}</span>
			</div>
			<input
				className="r-slider"
				type="range"
				min={min} max={max} step={step}
				value={value}
				style={{ "--pct": pct + "%" }}
				onChange={(e) => onChange(parseFloat(e.target.value))}
			/>
		</>
	);
}

function RangeDual({ label, min, max, step, lo, hi, onChange, format }) {
	const leftPct = ((lo - min) / (max - min)) * 100;
	const rightPct = ((hi - min) / (max - min)) * 100;
	return (
		<>
			<div className="range-label">
				<span>{label}</span>
				<span className="val">{format(lo, hi)}</span>
			</div>
			<div className="dual-range">
				<div className="track" />
				<div className="fill" style={{ left: leftPct + "%", right: 100 - rightPct + "%" }} />
				<input type="range" min={min} max={max} step={step} value={lo}
					onChange={(e) => {
						const v = Math.min(parseFloat(e.target.value), hi - step);
						onChange(v, hi);
					}} />
				<input type="range" min={min} max={max} step={step} value={hi}
					onChange={(e) => {
						const v = Math.max(parseFloat(e.target.value), lo + step);
						onChange(lo, v);
					}} />
			</div>
		</>
	);
}

function MuniSelect({ all, selected, onChange }) {
	const isSel = (m) => selected.includes(m);
	const toggle = (m) => {
		if (isSel(m)) onChange(selected.filter((x) => x !== m));
		else onChange([...selected, m]);
	};
	return (
		<>
			<div className="muni-list">
				{all.map((m) => (
					<button
						key={m}
						className={"muni-chip " + (isSel(m) ? "on" : "")}
						onClick={() => toggle(m)}
						title={m}>
						{m}
					</button>
				))}
			</div>
			<div className="muni-tools">
				<button onClick={() => onChange([])}>Clear all</button>
				<button onClick={() => onChange([...all])}>Select all</button>
			</div>
		</>
	);
}

const FILTER_CHIPS = [
	{
		key: "wind",
		label: "Wind",
		icon: <Wind size={14} />,
		isDefault: (f) => f.windSpeedMin == null,
		display: (f) => f.windSpeedMin == null ? "Any" : `≥ ${f.windSpeedMin.toFixed(2)} m/s`,
		title: "Minimum wind speed",
		desc: "Mean wind at 150 m hub height",
		reset: (f) => ({ ...f, windSpeedMin: null }),
		body: (f, set) => (
			<RangeSingle
				label="At hub height (150 m)"
				value={f.windSpeedMin ?? 6.0}
				min={6.0} max={9.0} step={0.05}
				onChange={(v) => set({ windSpeedMin: v })}
				format={(v) => f.windSpeedMin == null ? "Any" : `≥ ${v.toFixed(2)} m/s`}
			/>
		)
	},
	{
		key: "line",
		label: "Power line",
		icon: <Lightning size={14} />,
		isDefault: (f) => f.distLineMax == null,
		display: (f) => f.distLineMax == null ? "Any" : `≤ ${f.distLineMax.toFixed(1)} km`,
		title: "Distance to nearest power line",
		desc: "Closer connection = lower grid cost",
		reset: (f) => ({ ...f, distLineMax: null }),
		body: (f, set) => (
			<RangeSingle
				label="Maximum distance"
				value={f.distLineMax ?? 25}
				min={0.5} max={25} step={0.5}
				onChange={(v) => set({ distLineMax: v })}
				format={(v) => f.distLineMax == null ? "Any" : `≤ ${v.toFixed(1)} km`}
			/>
		)
	},
	{
		key: "substation",
		label: "Substation",
		icon: <Lightning size={14} />,
		isDefault: (f) => f.distSubstationMax == null,
		display: (f) => f.distSubstationMax == null ? "Any" : `≤ ${f.distSubstationMax.toFixed(1)} km`,
		title: "Distance to nearest substation",
		desc: "Closer substation = lower connection cost",
		reset: (f) => ({ ...f, distSubstationMax: null }),
		body: (f, set) => (
			<RangeSingle
				label="Maximum distance"
				value={f.distSubstationMax ?? 25}
				min={0.5} max={25} step={0.5}
				onChange={(v) => set({ distSubstationMax: v })}
				format={(v) => f.distSubstationMax == null ? "Any" : `≤ ${v.toFixed(1)} km`}
			/>
		)
	},
	{
		key: "nature",
		label: "Nature reserve",
		icon: <Tree size={14} />,
		isDefault: (f) => f.distNatureMin == null,
		display: (f) => f.distNatureMin == null ? "Any" : `≥ ${f.distNatureMin.toFixed(1)} km`,
		title: "Distance to nearest protected area",
		desc: "Nearest of SPA (birds) and SAC (habitats)",
		reset: (f) => ({ ...f, distNatureMin: null }),
		body: (f, set) => (
			<RangeSingle
				label="Minimum distance"
				value={f.distNatureMin ?? 0}
				min={0} max={20} step={0.5}
				onChange={(v) => set({ distNatureMin: v })}
				format={(v) => f.distNatureMin == null ? "Any" : `≥ ${v.toFixed(1)} km`}
			/>
		)
	},
	{
		key: "military",
		label: "Military",
		icon: <Shield size={14} />,
		isDefault: (f) => f.distMilitaryMin == null,
		display: (f) => f.distMilitaryMin == null ? "Any" : `≥ ${Math.round(f.distMilitaryMin)} km`,
		title: "Distance to nearest military area",
		desc: "Minimum buffer from restricted zones",
		reset: (f) => ({ ...f, distMilitaryMin: null }),
		body: (f, set) => (
			<RangeSingle
				label="Minimum distance"
				value={f.distMilitaryMin ?? 0}
				min={0} max={45} step={1}
				onChange={(v) => set({ distMilitaryMin: v })}
				format={(v) => f.distMilitaryMin == null ? "Any" : `≥ ${Math.round(v)} km`}
			/>
		)
	},
	{
		key: "area",
		label: "Area",
		icon: <Ruler size={14} />,
		isDefault: (f) => f.areaMin == null && f.areaMax == null,
		display: (f) =>
			f.areaMin == null && f.areaMax == null
				? "Any"
				: `${f.areaMin ?? 100}–${f.areaMax ?? 2000} ha`,
		title: "Area size",
		desc: "Hectares per candidate polygon",
		reset: (f) => ({ ...f, areaMin: null, areaMax: null }),
		body: (f, set) => (
			<RangeDual
				label="Hectares"
				min={100} max={2000} step={10}
				lo={f.areaMin ?? 100} hi={f.areaMax ?? 2000}
				onChange={(lo, hi) => set({ areaMin: lo, areaMax: hi })}
				format={(lo, hi) =>
					f.areaMin == null && f.areaMax == null ? "Any" : `${lo} – ${hi} ha`}
			/>
		)
	},
	{
		key: "muni",
		label: "Municipality",
		icon: <MapPin size={14} />,
		isDefault: (f) => f.municipalities.length === 0,
		display: (f) =>
			f.municipalities.length === 0 ? "Any" :
			f.municipalities.length === 1 ? f.municipalities[0] :
			`${f.municipalities.length} selected`,
		title: "Municipality",
		desc: "Restrict to one or more municipalities",
		reset: (f) => ({ ...f, municipalities: [] }),
		body: (f, set, ctx) => (
			<MuniSelect
				all={(ctx && ctx.municipalities) || []}
				selected={f.municipalities}
				onChange={(arr) => set({ municipalities: arr })}
			/>
		)
	}
];

function FilterChipBar({ filters, setFilters, openKey, setOpenKey }) {
	return (
		<div className="filter-chip-row">
			{FILTER_CHIPS.map((chip) => {
				const mod = !chip.isDefault(filters);
				const isOpen = openKey === chip.key;
				return (
					<button
						key={chip.key}
						className={"f-chip " + (isOpen ? "active " : "") + (mod ? "modified" : "")}
						onClick={() => setOpenKey(isOpen ? null : chip.key)}>
						<span className="icon">{chip.icon}</span>
						<span className="lbl">{chip.label}</span>
						<span className="v">{chip.display(filters)}</span>
						{mod && (
							<span
								className="clear-x"
								title="Clear"
								onClick={(e) => {
									e.stopPropagation();
									setFilters(chip.reset(filters));
								}}>
								<X size={12} weight="bold" />
							</span>
						)}
					</button>
				);
			})}
		</div>
	);
}

/* ---- Screening Panel ---- */
function ScreeningPanel({
	filters, setFilters,
	matching, totalCount,
	selectedIds, setSelectedIds,
	onFlyTo,
	dataset, setDataset,
	municipalities,
	header,
	footer
}) {
	const [openKey, setOpenKey] = useState(null);
	const matchCount = matching.length;
	const modifiedCount = FILTER_CHIPS.filter((c) => !c.isDefault(filters)).length;
	const activeChip = FILTER_CHIPS.find((c) => c.key === openKey);
	const set = (patch) => setFilters({ ...filters, ...patch });
	const sorted = useMemo(
		() => [...matching].sort((a, b) => b.properties.score - a.properties.score),
		[matching]
	);

	return (
		<aside className={"screening-panel " + (header ? "np-embedded" : "")}>
			{header}
			<div className={header ? "sp-subheader" : "sp-header"}>
				{!header && <div className="icn-bubble"><MagnifyingGlass size={18} /></div>}
				<div>
					{!header && <h1>Screening</h1>}
					<div className="sub">
						Candidate wind-farm areas in Bulgaria ·{" "}
						<select
							className="country-select"
							value={dataset}
							onChange={(e) => setDataset(e.target.value)}
							aria-label="Dataset">
							{Object.keys(DATASETS).map((k) => (
								<option key={k} value={k}>{DATASETS[k].label}</option>
							))}
						</select>
					</div>
				</div>
			</div>

			<div className="sp-filter">
				<div className="sp-filter-head">
					<span className="ttl">
						Filters
						{modifiedCount > 0 && <span className="badge">{modifiedCount}</span>}
					</span>
					<button
						className="reset"
						disabled={modifiedCount === 0}
						onClick={() => { setFilters(FILTER_DEFAULTS); setOpenKey(null); }}>
						Reset all
					</button>
				</div>
				<FilterChipBar filters={filters} setFilters={setFilters} openKey={openKey} setOpenKey={setOpenKey} />
				{activeChip && (
					<div className="sp-expand">
						<div className="pop-title">
							<span className="icon">{activeChip.icon}</span>
							{activeChip.title}
						</div>
						<div className="pop-desc">{activeChip.desc}</div>
						{activeChip.body(filters, set, { municipalities })}
						<div className="pop-footer">
							<button className="pop-clear" onClick={() => setFilters(activeChip.reset(filters))}>
								Clear
							</button>
							<button className="btn btn-primary btn-sm" onClick={() => setOpenKey(null)}>
								Done
							</button>
						</div>
					</div>
				)}
			</div>

			<div className="sp-results">
				<div className="left">
					<span className="n">{matchCount}</span>
					<span className="lbl">of {totalCount} matching</span>
				</div>
				<div className="right">
					{selectedIds.length > 0 && (
						<span className="selected-pill">
							<span className="dot" />
							{selectedIds.length} selected
						</span>
					)}
					<span className="sort">sorted by score</span>
				</div>
			</div>

			<div className="sp-list">
				{sorted.length === 0 && (
					<div className="sp-list-empty">
						No areas match the current filters.<br />
						Try relaxing one of the constraints.
					</div>
				)}
				{sorted.map((f) => {
					const p = f.properties;
					const sel = selectedIds.includes(p.id);
					return (
						<div
							key={p.id}
							className={"area-row " + (sel ? "selected" : "")}
							onClick={() => {
								if (sel) setSelectedIds(selectedIds.filter((x) => x !== p.id));
								else setSelectedIds([...selectedIds, p.id]);
								onFlyTo && onFlyTo(p.id);
							}}>
							<div className="top">
								<span className="nm">{p.name}</span>
								<span className="score-badge">{p.score}</span>
							</div>
							<div className="area-meta">
								<span><b>{p.windSpeed.toFixed(1)}</b> m/s</span>
								<span><b>{p.areaHa}</b> ha</span>
								<span><b>{p.distLine.toFixed(1)}</b> km grid</span>
							</div>
						</div>
					);
				})}
			</div>
			{footer}
		</aside>
	);
}

/* ---- Selection bar ----
   Floats over the map, or sits at the bottom of the panel (inPanel) when the
   overlay is embedded in the sandbox's New project panel. */
function SelectionBar({ selectedFeatures, onClear, onCreate, inPanel = false }) {
	if (selectedFeatures.length === 0) return null;
	const totalHa = selectedFeatures.reduce((s, f) => s + f.properties.areaHa, 0);
	const avgWind = selectedFeatures.reduce((s, f) => s + f.properties.windSpeed, 0) / selectedFeatures.length;
	return (
		<div className={"selection-bar " + (inPanel ? "in-panel" : "")}>
			<div className="sel-summary">
				<span className="sel-count">
					{selectedFeatures.length} area{selectedFeatures.length > 1 ? "s" : ""} selected
				</span>
				<span className="sel-meta">
					{totalHa.toLocaleString()} ha total · avg {avgWind.toFixed(1)} m/s
				</span>
			</div>
			<button className="btn btn-ghost btn-sm" onClick={onClear}>Clear</button>
			<button className="btn btn-success" onClick={onCreate}>
				<Plus size={16} /> Create project
			</button>
		</div>
	);
}

/* ---- Create Project Modal ---- */

function CreateProjectModal({ selectedFeatures, user, onClose, onCreated }) {
	const navigate = useNavigate();
	const [step, setStep] = useState("form");
	const [mode, setMode] = useState("combined"); // "combined" | "separate"
	const [progress, setProgress] = useState(0);
	const [error, setError] = useState(null);
	const multi = selectedFeatures.length > 1;

	const [form, setForm] = useState({
		name: selectedFeatures.length === 1
			? selectedFeatures[0].properties.name
			: `Bulgaria (${selectedFeatures.length} areas)`
	});

	const separate = multi && mode === "separate";
	const projectName = (p) => p.name;

	const totalHa = selectedFeatures.reduce((s, f) => s + f.properties.areaHa, 0);
	const avgWind = selectedFeatures.length
		? selectedFeatures.reduce((s, f) => s + f.properties.windSpeed, 0) / selectedFeatures.length
		: 0;
	const potentialMW = Math.round(totalHa * 0.05);

	const handleSave = async () => {
		setStep("saving");
		setError(null);
		const base = {
			country: "bulgaria",
			energyTypes: ["wind"]
		};
		try {
			if (separate) {
				// The API only accepts one project per request, so create them sequentially.
				for (let i = 0; i < selectedFeatures.length; i++) {
					const f = selectedFeatures[i];
					setProgress(i + 1);
					const res = await atlasApi.createProject({
						...base,
						name: projectName(f.properties),
						boundary: toMultiPolygon([f])
					});
					if (!res || !res.projectid) throw new Error("No projectid returned");
				}
			} else {
				const res = await atlasApi.createProject({
					...base,
					name: form.name,
					boundary: dissolveToMultiPolygon(selectedFeatures)
				});
				if (!res || !res.projectid) throw new Error("No projectid returned");
			}
			onCreated();
			navigate("/pages/projects");
		} catch (e) {
			setError(separate && progress > 1
				? `Failed while creating project ${progress} of ${selectedFeatures.length}. The first ${progress - 1} may already have been created.`
				: "Could not create the project. Please try again.");
			setStep("form");
		}
	};

	return (
		<div className="screening-modal-backdrop" onClick={onClose}>
			<div className="screening-modal" onClick={(e) => e.stopPropagation()}>
				{step !== "done" && (
					<div className="modal-header">
						<h2>Create Energy Atlas project</h2>
						<div className="sub">
							{selectedFeatures.length} screening area{selectedFeatures.length > 1 ? "s" : ""}
							{" · "}{totalHa.toLocaleString()} ha
							{separate && ` · ${selectedFeatures.length} projects`}
						</div>
					</div>
				)}

				{step === "form" && (
					<>
						<div className="modal-body">
							{error && (
								<div style={{
									background: "#fef3f2", border: "1px solid #fda29b", color: "#b42318",
									borderRadius: 8, padding: "10px 12px", fontSize: 13, marginBottom: 14
								}}>
									{error}
								</div>
							)}
							{multi && (
								<div className="form-row">
									<label>Create as</label>
									<div className="mode-toggle">
										<button
											type="button"
											className={mode === "combined" ? "on" : ""}
											onClick={() => setMode("combined")}>
											<b>One combined project</b>
											<span>All {selectedFeatures.length} areas in a single project</span>
										</button>
										<button
											type="button"
											className={mode === "separate" ? "on" : ""}
											onClick={() => setMode("separate")}>
											<b>{selectedFeatures.length} separate projects</b>
											<span>One project per area</span>
										</button>
									</div>
								</div>
							)}
							{!separate && (
								<div className="form-row">
									<label>Project name</label>
									<input className="form-control" value={form.name}
										onChange={(e) => setForm({ ...form, name: e.target.value })} />
								</div>
							)}

							<label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "var(--neutral-700)", marginBottom: 4 }}>
								{separate ? "Projects to create" : "Areas included"}
							</label>
							<div className="selected-summary-list">
								{selectedFeatures.map((f) => {
									const p = f.properties;
									return (
										<div key={p.id} className="row">
											<span className="nm">{separate ? projectName(p) : p.name} <span style={{ color: "var(--neutral-400)", fontWeight: 500 }}>· {p.id}</span></span>
											<span className="meta">
												<span>{p.areaHa} ha</span>
												<span>{p.windSpeed.toFixed(1)} m/s</span>
												<span>score {p.score}</span>
											</span>
										</div>
									);
								})}
							</div>

							<div className="totals-grid">
								<div className="ttl">
									<label>Total area</label>
									<div className="vv">{totalHa.toLocaleString()} ha</div>
								</div>
								<div className="ttl">
									<label>Avg wind</label>
									<div className="vv">{avgWind.toFixed(1)} m/s</div>
								</div>
								<div className="ttl">
									<label>Potential</label>
									<div className="vv">~{potentialMW} MW</div>
								</div>
							</div>
						</div>
						<div className="modal-footer">
							<button className="btn btn-outline" onClick={onClose}>Cancel</button>
							<button className="btn btn-success" onClick={handleSave}>
								{separate ? `Create ${selectedFeatures.length} projects` : "Create project"}
							</button>
						</div>
					</>
				)}

				{step === "saving" && (
					<div className="modal-body" style={{ textAlign: "center", padding: "60px 24px" }}>
						<div className="spin-ring" />
						<p style={{ color: "var(--neutral-600)", marginTop: 14 }}>
							{separate
								? `Creating project ${progress} of ${selectedFeatures.length}…`
								: "Setting up project boundaries…"}
						</p>
					</div>
				)}
			</div>
		</div>
	);
}
function centroid(ring) {
	let x = 0, y = 0, n = ring.length - 1;
	for (let i = 0; i < n; i++) { x += ring[i][0]; y += ring[i][1]; }
	return [x / n, y / n];
}

/* ---- Map layers ---- */
function addScreeningLayers(map, fcInit, baseLayers = true) {
	if (!map.getSource("scr-areas")) {
		map.addSource("scr-areas", { type: "geojson", data: fcInit });
	} else {
		map.getSource("scr-areas").setData(fcInit);
	}
	if (!map.getLayer("scr-areas-fill")) {
		map.addLayer({
			id: "scr-areas-fill",
			type: "fill",
			source: "scr-areas",
			paint: {
				"fill-color": ["case",
					["==", ["get", "_selected"], 1], "#f79009",
					["==", ["get", "_match"], 1], "#12b76a",
					"#9aa3b0"
				],
				"fill-opacity": ["case",
					["==", ["get", "_match"], 1], 0.42,
					0.10
				]
			}
		});
	}
	if (!map.getLayer("scr-areas-line")) {
		map.addLayer({
			id: "scr-areas-line",
			type: "line",
			source: "scr-areas",
			paint: {
				"line-color": ["case",
					["==", ["get", "_selected"], 1], "#b54708",
					["==", ["get", "_match"], 1], "#027a48",
					"#717680"
				],
				"line-width": ["case",
					["==", ["get", "_selected"], 1], 2.6,
					["==", ["get", "_match"], 1], 1.8,
					0.8
				],
				"line-opacity": ["case",
					["==", ["get", "_match"], 1], 1,
					0.4
				]
			}
		});
	}
	if (!map.getLayer("scr-areas-label")) {
		map.addLayer({
			id: "scr-areas-label",
			type: "symbol",
			source: "scr-areas",
			filter: ["==", ["get", "_match"], 1],
			layout: {
				"text-field": ["get", "id"],
				"text-size": 11,
				"text-font": ["Open Sans Semibold", "Arial Unicode MS Bold"],
				"text-allow-overlap": false
			},
			paint: {
				"text-color": "#12374f",
				"text-halo-color": "rgba(255,255,255,0.95)",
				"text-halo-width": 1.5
			}
		});
	}
	if (baseLayers) addHighVoltageLines(map);
}

function removeScreeningLayers(map) {
	if (!map || !map.getStyle) return;
	try {
		removePrioritizedZones(map);
		["scr-areas-label", "scr-areas-line", "scr-areas-fill", "scr-high-voltage-line"].forEach((id) => {
			if (map.getLayer(id)) map.removeLayer(id);
		});
		["scr-areas", "scr-high-voltage"].forEach((id) => {
			if (map.getSource(id)) map.removeSource(id);
		});
	} catch (e) { /* style may already be gone */ }
}

/* ---- Overlay ----
   Renders the screening panel, selection bar and create-project modal on top of
   an existing mapbox map. Used both by the Screening page and by sandbox maps.

   map        : mapboxgl.Map instance (or null while it is being created)
   mapReady   : true once the map style has loaded
   visible    : show/hide the whole overlay (layers are removed when hidden)
   styleKey   : bump/change whenever the basemap style is swapped, so the
                layers get re-added to the new style
   fitPadding : padding used when fitting the map to the dataset
   fitOnShow  : fit the map to the dataset every time the overlay is shown
   baseLayers : also draw the supporting reference layers (high voltage grid and
                prioritized zone outlines). Off for sandbox maps, where the user
                adds those layers from the layer library instead.
   header     : replaces the panel's own header (the sandbox's New project panel
                passes its title + method tabs here)
   onDirtyChange : called with true/false as the selection becomes (non-)empty
*/
export default function ScreeningOverlay({
	map,
	mapReady,
	visible = true,
	styleKey,
	className = "",
	fitPadding = { top: 60, bottom: 60, left: 60, right: 420 },
	fitOnShow = false,
	baseLayers = true,
	onLoadingChange,
	onCreated,
	header,
	onDirtyChange
}) {
	const popupRef = useRef(null);
	const fittedRef = useRef(false);
	const loadedDatasetRef = useRef(null);
	const [data, setData] = useState({ type: "FeatureCollection", features: [] });
	const [dataset, setDataset] = useState("all");
	const [filters, setFilters] = useState(FILTER_DEFAULTS);
	const [selectedIds, setSelectedIds] = useState([]);
	const [modalOpen, setModalOpen] = useState(false);

	// Load dataset (re-runs when dataset changes). Nothing is fetched until the
	// overlay is shown for the first time.
	useEffect(() => {
		if (!visible || loadedDatasetRef.current === dataset) return;
		let alive = true;
		const cfg = DATASETS[dataset] || DATASETS.all;
		onLoadingChange && onLoadingChange(false);
		fetch(cfg.file)
			.then((r) => r.json())
			.catch(() => ({ features: [] }))
			.then((geo) => {
				if (!alive) return;
				setData(normalizeFeatures(geo, cfg.idPrefix));
				setSelectedIds([]);
				setFilters(FILTER_DEFAULTS);
				fittedRef.current = false;
				loadedDatasetRef.current = dataset;
				onLoadingChange && onLoadingChange(true);
			});
		return () => { alive = false; };
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dataset, visible]);

	const fc = useMemo(() => {
		const features = data.features.map((f) => {
			const matches = passes(f.properties, filters);
			const selected = selectedIds.includes(f.properties.id);
			return {
				...f,
				properties: { ...f.properties, _match: matches ? 1 : 0, _selected: selected ? 1 : 0 }
			};
		});
		return { type: "FeatureCollection", features };
	}, [data, filters, selectedIds]);

	const matching = useMemo(
		() => data.features.filter((f) => passes(f.properties, filters)),
		[data, filters]
	);

	const selectedFeatures = useMemo(
		() => data.features.filter((f) => selectedIds.includes(f.properties.id)),
		[data, selectedIds]
	);

	const municipalities = useMemo(() => {
		const s = new Set();
		data.features.forEach((f) => s.add(f.properties.municipality));
		return [...s].sort((a, b) => a.localeCompare(b, "bg"));
	}, [data]);

	const showPopup = useCallback((m, feature) => {
		if (popupRef.current) popupRef.current.remove();
		const p = feature.properties;
		const isSel = selectedIds.includes(p.id);

		const root = document.createElement("div");
		root.className = "area-popup";
		root.innerHTML = `
			<div class="ap-header">
				<div class="ap-sub">${p.municipality} · ${p.id}</div>
				<div class="ap-title">${p.name}</div>
			</div>
			<div class="ap-body">
				<div class="ap-score">
					<div>
						<div class="lbl">Suitability score</div>
						<div style="font-size:11px;color:var(--neutral-600);">composite of all filters</div>
					</div>
					<div class="vbig">${p.score}</div>
				</div>
				<div class="ap-stats">
					<div class="ap-stat"><label>Wind @150m</label><div class="v">${(+p.windSpeed).toFixed(1)} m/s</div></div>
					<div class="ap-stat"><label>Area</label><div class="v">${Number(p.areaHa).toLocaleString()} ha</div></div>
					<div class="ap-stat"><label>Power line</label><div class="v">${(+p.distLine).toFixed(1)} km</div></div>
					<div class="ap-stat"><label>Substation</label><div class="v">${(+p.distSubstation).toFixed(1)} km</div></div>
					<div class="ap-stat"><label>Protected area</label><div class="v">${(+p.distNatureReserve).toFixed(1)} km</div></div>
					<div class="ap-stat"><label>Military</label><div class="v">${(+p.distMilitary).toFixed(1)} km</div></div>
					<div class="ap-stat"><label>Settlement</label><div class="v">${(+p.distSettlement).toFixed(1)} km</div></div>
					<div class="ap-stat"><label>Forest</label><div class="v">${Math.round(+p.forestPct)} %</div></div>
				</div>
				<div class="ap-btn-row">
					<button class="btn btn-outline" data-action="dismiss">Close</button>
					<button class="btn ${isSel ? "btn-outline" : "btn-primary"}" data-action="toggle">
						${isSel ? "Remove from selection" : "Add to selection"}
					</button>
				</div>
			</div>
		`;

		const center = centroid(feature.geometry.coordinates[0]);
		const popup = new mapboxgl.Popup({
			closeButton: true,
			closeOnClick: false,
			maxWidth: "320px",
			offset: 12
		})
			.setLngLat(center)
			.setDOMContent(root)
			.addTo(m);
		popupRef.current = popup;

		root.querySelector('[data-action="dismiss"]').addEventListener("click", () => popup.remove());
		root.querySelector('[data-action="toggle"]').addEventListener("click", () => {
			if (selectedIds.includes(p.id)) {
				setSelectedIds(selectedIds.filter((x) => x !== p.id));
			} else {
				setSelectedIds([...selectedIds, p.id]);
			}
			popup.remove();
		});
	}, [selectedIds]);

	const flyToFeature = useCallback((id) => {
		if (!map) return;
		const f = data.features.find((x) => x.properties.id === id);
		if (!f) return;
		const coords = f.geometry.type === "Polygon"
			? f.geometry.coordinates[0]
			: f.geometry.coordinates.flat(1).flat(0);
		const xs = coords.map((p) => p[0]);
		const ys = coords.map((p) => p[1]);
		map.fitBounds(
			[[Math.min(...xs), Math.min(...ys)], [Math.max(...xs), Math.max(...ys)]],
			{ padding: 120, duration: 700, maxZoom: 13 }
		);
	}, [map, data]);

	useEffect(() => {
		onDirtyChange && onDirtyChange(selectedIds.length > 0);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedIds.length]);

	// Add / remove the screening layers
	useEffect(() => {
		if (!map || !mapReady) return;
		if (!visible) {
			if (popupRef.current) { popupRef.current.remove(); popupRef.current = null; }
			removeScreeningLayers(map);
			fittedRef.current = false;
			return;
		}
		let cancelled = false;
		const ensure = () => {
			if (cancelled) return;
			addScreeningLayers(map, fc, baseLayers);
			if (baseLayers && dataset === "prioritized") addPrioritizedZones(map);
			else removePrioritizedZones(map);
		};
		if (map.isStyleLoaded()) ensure();
		else map.once("styledata", ensure);
		return () => { cancelled = true; };
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [map, mapReady, visible, styleKey, dataset, baseLayers]);

	// Remove the layers when the overlay goes away entirely
	useEffect(() => {
		return () => {
			if (popupRef.current) { popupRef.current.remove(); popupRef.current = null; }
		};
	}, []);

	// Push data changes to the map
	useEffect(() => {
		if (!map || !mapReady || !visible) return;
		const src = map.getSource("scr-areas");
		if (src) src.setData(fc);
	}, [map, mapReady, visible, fc]);

	// Fit the map to the dataset the first time it is shown
	useEffect(() => {
		if (!map || !mapReady || !visible || !data.features.length) return;
		if (fittedRef.current) return;
		fittedRef.current = true;
		try {
			const bounds = new mapboxgl.LngLatBounds();
			data.features.forEach((f) => {
				if (f.geometry.type !== "Polygon") return;
				f.geometry.coordinates[0].forEach((c) => bounds.extend(c));
			});
			if (!bounds.isEmpty()) {
				map.fitBounds(bounds, { padding: fitPadding, duration: fitOnShow ? 700 : 0, maxZoom: 10 });
			}
		} catch (e) { /* noop */ }
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [map, mapReady, visible, data]);

	// Click handlers
	useEffect(() => {
		if (!map || !mapReady || !visible) return;
		const onClick = (e) => {
			const f = e.features && e.features[0];
			if (!f) return;
			// pull the full feature from data so geometry is the original
			const orig = data.features.find((x) => x.properties.id === f.properties.id) || f;
			showPopup(map, orig);
		};
		const onEnter = () => { map.getCanvas().style.cursor = "pointer"; };
		const onLeave = () => { map.getCanvas().style.cursor = ""; };
		map.on("click", "scr-areas-fill", onClick);
		map.on("mouseenter", "scr-areas-fill", onEnter);
		map.on("mouseleave", "scr-areas-fill", onLeave);
		return () => {
			map.off("click", "scr-areas-fill", onClick);
			map.off("mouseenter", "scr-areas-fill", onEnter);
			map.off("mouseleave", "scr-areas-fill", onLeave);
		};
	}, [map, mapReady, visible, data, showPopup]);

	if (!visible) return null;

	const selectionBar = (
		<SelectionBar
			selectedFeatures={selectedFeatures}
			onClear={() => setSelectedIds([])}
			onCreate={() => setModalOpen(true)}
			inPanel={!!header}
		/>
	);

	return (
		<>
			<div className={"screening-main screening-overlay " + className}>
				<ScreeningPanel
					filters={filters}
					setFilters={setFilters}
					matching={matching}
					totalCount={data.features.length}
					selectedIds={selectedIds}
					setSelectedIds={setSelectedIds}
					onFlyTo={flyToFeature}
					dataset={dataset}
					setDataset={setDataset}
					municipalities={municipalities}
					header={header}
					footer={header ? selectionBar : null}
				/>

				{!header && selectionBar}
			</div>

			{modalOpen && (
				<CreateProjectModal
					selectedFeatures={selectedFeatures}
					onClose={() => setModalOpen(false)}
					onCreated={() => { setSelectedIds([]); onCreated && onCreated(); }}
				/>
			)}
		</>
	);
}
